import {
  forwardRef,
  Fragment,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react'
import { createPortal } from 'react-dom'
import PropTypes from 'prop-types'
import { FilterContext } from 'features/dashboard/filter/FilterContext'
import { useSelector } from 'react-redux'
import {
  FilterMenus,
  FilterOperators,
} from 'features/dashboard/filter/FilterSpecs'
import NameFilterMenu from 'features/dashboard/filter/menu/NameFilterMenu'
import FilterMenu from 'features/dashboard/filter/menu/FilterMenu'
import FilterSubMenu from 'features/dashboard/filter/menu/FilterSubMenu'
import IdFilterMenu from 'features/dashboard/filter/menu/IdFilterMenu'
import FilterHistories from 'features/dashboard/filter/menu/FilterHistories'
import classNames from 'classnames'
import SavedFilters from 'features/dashboard/filter/menu/SavedFilters'
import SavedFilterNames from 'features/dashboard/filter/menu/SavedFilterNames'

IdFilterMenu.propTypes = {
  menu: PropTypes.shape({
    domain: PropTypes.string,
    title: PropTypes.string,
  }),
}

const FilterMenuLayer = forwardRef(({ inputRef }, ref) => {
  const { filters } = useSelector(state => state.dashboard.filter)
  const { left, bottom } = inputRef?.current?.getBoundingClientRect() || {
    left: 0,
    bottom: 0,
  }
  const { value, selectedMenu, setSelectedMenu, menuVisible, setMenuVisible } =
    useContext(FilterContext)
  const handleOutsideClick = event => {
    if (ref.current && !ref.current.contains(event.target)) {
      if (value.trim() === '') {
        setMenuVisible(false)
      }
    }
  }
  const scrollRef = useRef()

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 0 })
  }, [selectedMenu])

  useEffect(() => {
    document.addEventListener('mousedown', handleOutsideClick)
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick)
    }
  })

  const container = document.getElementById('kakaoContent')
  const { top: containerTop, left: containerLeft } = container
    ? container.getBoundingClientRect()
    : {
        top: 0,
        left: 0,
      }
  const { filterEnabled } = useSelector(state => state.dashboard.common)
  const offsetY = filterEnabled ? 8 : 0
  const visibleSubMenus = useMemo(
    () =>
      selectedMenu?.domain
        ? selectedMenu.domain === 'creativeLink'
          ? selectedMenu.subs.filter(({ key }) => key !== 'asset')
          : selectedMenu.subs
        : [],
    [selectedMenu]
  )
  return container ? (
    createPortal(
      <div
        ref={ref}
        className={
          filterEnabled
            ? 'layer_adsearch'
            : 'tooltip_basic show place-bottom ly_tooltip2'
        }
        style={{
          display: inputRef.current && menuVisible ? 'block' : 'none',
          position: 'absolute',
          top: bottom - containerTop + offsetY,
          left: left - containerLeft + 4,
        }}
        onMouseLeave={() => setSelectedMenu(undefined)}>
        {filterEnabled ? (
          <div className="inner_g">
            {filters.length === 30 ? (
              <div className="tooltip_basic ly_tooltip">
                <p className="txt_error">최대 30개 필터만 입력 가능합니다.</p>
              </div>
            ) : (
              <>
                <div className="item_depth">
                  {value.trim() !== '' ? (
                    <>
                      <strong className="tit_lyadsearch">추천 검색</strong>
                      <ul className="list_lyadsearch">
                        <NameFilterMenu
                          menu={FilterMenus.campaign}
                          operator={FilterOperators.like}
                        />
                        <NameFilterMenu
                          menu={FilterMenus.adGroup}
                          operator={FilterOperators.like}
                        />
                        <NameFilterMenu
                          menu={FilterMenus.keyword}
                          operator={FilterOperators.like}
                        />
                        <NameFilterMenu
                          menu={FilterMenus.creativeLink}
                          operator={FilterOperators.like}
                        />
                        <NameFilterMenu
                          menu={FilterMenus.campaign}
                          operator={FilterOperators.eq}
                        />
                        <NameFilterMenu
                          menu={FilterMenus.adGroup}
                          operator={FilterOperators.eq}
                        />
                        <NameFilterMenu
                          menu={FilterMenus.keyword}
                          operator={FilterOperators.eq}
                        />
                        <NameFilterMenu
                          menu={FilterMenus.creativeLink}
                          operator={FilterOperators.eq}
                        />
                        {/^[0-9]+$/.test(value) && value.length < 19 && (
                          <>
                            <IdFilterMenu menu={FilterMenus.campaign} />
                            <IdFilterMenu menu={FilterMenus.adGroup} />
                            <IdFilterMenu menu={FilterMenus.keyword} />
                            <IdFilterMenu menu={FilterMenus.creativeLink} />
                          </>
                        )}
                      </ul>
                      <SavedFilterNames />
                    </>
                  ) : (
                    <>
                      <ul className="list_lyadsearch">
                        <FilterMenu menu={FilterMenus.recent} />
                        <FilterMenu menu={FilterMenus.saved} />
                      </ul>
                      <strong className="tit_lyadsearch">필터</strong>
                      <ul className="list_lyadsearch">
                        <FilterMenu menu={FilterMenus.campaign} />
                        <FilterMenu menu={FilterMenus.adGroup} />
                        <FilterMenu menu={FilterMenus.keyword} />
                        <FilterMenu menu={FilterMenus.creativeLink} />
                      </ul>
                    </>
                  )}
                </div>
                {value.trim() === '' && selectedMenu && (
                  <div
                    ref={scrollRef}
                    className={classNames('item_depth2', {
                      item_savelist:
                        selectedMenu === FilterMenus.recent ||
                        selectedMenu === FilterMenus.saved,
                    })}>
                    {[
                      FilterMenus.campaign,
                      FilterMenus.adGroup,
                      FilterMenus.keyword,
                      FilterMenus.creativeLink,
                    ].includes(selectedMenu) && (
                      <ul className="list_lyadsearch">
                        {visibleSubMenus.map(sub => (
                          <FilterSubMenu key={sub.key} sub={sub} />
                        ))}
                      </ul>
                    )}
                    {selectedMenu === FilterMenus.recent && <FilterHistories />}
                    {selectedMenu === FilterMenus.saved && <SavedFilters />}
                  </div>
                )}
              </>
            )}
          </div>
        ) : (
          <>
            <strong className="tit_tooltip">필터 기능 시스템 점검중</strong>
            <p className="txt_tooltip3">
              서비스 이용에 불편을 드려 죄송합니다.
              <br />
              빠른 시간내 점검을 완료 하겠습니다.
            </p>
          </>
        )}
      </div>,
      container
    )
  ) : (
    <Fragment />
  )
})
FilterMenuLayer.displayName = 'FilterMenuLayer'
FilterMenuLayer.propTypes = {
  inputRef: PropTypes.shape({
    current: PropTypes.object,
  }),
}
export default FilterMenuLayer
